import type { Nullable, TechnicalData } from '@models/data';
import type { AttributeDefinitionFragment, Cf_Colour_Codes } from '@generated/graphql';
import Router from 'next/router';
import type { ProductFragmentEnhanced, TechnicalDataRecordFragmentEnhanced } from '@models/enhanced-fragments';
import { getAttributeDisplayValue, getAttributeValue, isLabeledAttribute } from './attribute.helper';
import type { ProductAttribute, ProductAttributeUnion, ProductAttributeValueAmountUnit } from './attribute.helper';
import { ProductHelper } from '@models/graphql';
import type { TechnicalDataCategory } from '@product/ProductTabs/tabs/TechnicalDataTab/useTechnicalDataQuery';
import { WidenUtil } from './widen-utils';
import { WidenImageConfig } from '@models/widen-image-config';
import type { UnitSystem } from '@icommerce/units';

export type TechnicalDataAttributeLegacyConfig =
  | { id: string }
  | { id: string; icon: string }
  | { id: string; replaceIconMap: Record<string, string>; fallbackIcon: string };

export function isTechnicalDataAttributeAndNotLegacyConfig(
  attribute: TechnicalDataAttribute | TechnicalDataAttributeLegacyConfig
): attribute is TechnicalDataAttribute {
  return 'widenUrl' in attribute;
}

export type TechnicalDataAttribute = {
  id: string;
  widenUrl: string | null;
  assetAttributeId?: string;
  widenId?: string | null;
};

export type TechnicalDataAttributes = (TechnicalDataAttribute | TechnicalDataAttributeLegacyConfig)[];

const TECHNICAL_CHAINFLEX_CLASS_ID_ATTRIBUTES: TechnicalDataAttributes = [
  { id: 'cf_class_basic_requirement_id' },
  { id: 'cf_class_travel_length_id' },
  { id: 'cf_class_oil_resistance_id' },
  { id: 'cf_class_torsion_id' },
];

const TECHNICAL_CHAINFLEX_CLASS_LABEL_ATTRIBUTES: TechnicalDataAttributes = [
  { id: 'cf_class_basic_requirement' },
  { id: 'cf_class_travel_length' },
  { id: 'cf_class_oil_resistance' },
  { id: 'cf_class_torsion' },
];

const TECHNICAL_MECHANICAL_ATTRIBUTES: TechnicalDataAttributes = [
  { id: 'axial_load_static' },
  { id: 'axial_load_dynamic' },
  { id: 'radial_load_static' },
  { id: 'radial_load_dynamic' },
  { id: 'rotating_speed_dry_running' },
  { id: 'max_permissible_tilt_moment' },
  { id: 'transmission_simple_select' },
  { id: 'efficiency_text' },
  { id: 'modulus_of_elasticity' },
  { id: 'modulus_of_elasticity_testing_method_text' },
  { id: 'bending_strength' },
  { id: 'bending_strength_testing_method_text' },
  { id: 'rated_torque_motor_metric' },
  { id: 'rateted_speed_motor_number' },
  { id: 'ratio_i_simple_select' },
  { id: 'nominal_output_torque_metric' },
  { id: 'ratchet_torque_motors_metric' },
  { id: 'attr_holding_torque_motor_metric' },
  { id: 'gear_type_simple_select' },
  { id: 'max_drive_speed_at_operating_temperature_minus_10_90_degree_celsius_number' },
];

const TECHNICAL_REQUIREMENTS_ATTRIBUTES: TechnicalDataAttributes = [
  {
    id: 'fda_compliant',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('lhkruj4byr', 'Picto_FDA.webp', 300, 300)),
  },
  { id: 'food_contact_FDA' },
  { id: 'eu_ten_twentyeleven_compliant' },
  { id: 'model_text' },
];

const TECHNICAL_GENERAL_ATTRIBUTES: TechnicalDataAttributeLegacyConfig[] = [
  { id: 'number_of_teeth_simple_select' },
  { id: 'modul_simple_select' },
  { id: 'attr_material_text', icon: 'ai929.png' },
  { id: 'attr_fibre_type_simple_select' },
  { id: 'attr_fibre_identification_simple_select' },
  { id: 'igus_article_description_simple_select' },
  { id: 'attr_dimensions_text', icon: 'ai916.png' },
  {
    id: 'attr_operating_pressure_metric_bar',
    icon: 'ai918.png',
  },
  {
    id: 'vacuum_at_20_68_degree',
    icon: 'ai922.png',
  },
  {
    id: 'net_weight_metric',
  },
  {
    id: 'max_gross_weight_metric',
  },
  {
    id: 'max_cable_length_metric',
  },
  {
    id: 'attr_number_of_fibres_number_pcs',
  },
  {
    id: 'Faserdurchmesser ca.',
  },
  {
    id: 'attr_fibre_type_simple_select',
  },
  {
    id: 'attr_fibre_identification_simple_select',
  },
  {
    id: 'attr_bus_type_simple_select',
  },
  {
    id: 'attr_number_of_cores_and_conductor_nominal_cross_section_simple_select',
  },
  {
    id: 'attr_earth_core_simple_select',
  },
  {
    id: 'attr_copper_index_metric_kg_km',
  },
  {
    id: 'attr_thermo_material_simple_select',
  },
  {
    id: 'colour_code',
  },
  {
    id: 'attr_core_group_1_simple_select',
  },
  {
    id: 'core_group_colour_code_1_text',
  },
  {
    id: 'attr_core_group_2_simple_select',
  },
  {
    id: 'core_group_colour_code_2_text',
  },
  {
    id: 'attr_core_group_3_simple_select',
  },
  {
    id: 'core_group_colour_code_3_text',
  },
  {
    id: 'attr_core_group_4_simple_select',
  },
  {
    id: 'core_group_colour_code_4_text',
  },
  {
    id: 'attr_bandwidth_01_text',
  },
  {
    id: 'attr_attenuation_01_text',
  },
  {
    id: 'attr_bandwidth_02_text',
  },
  {
    id: 'attr_attenuation_02_text',
  },
  {
    id: 'attr_bandwidth_03_text',
  },
  {
    id: 'attr_attenuation_03_text',
  },
  {
    id: 'attr_attenuation_04_text',
  },
  {
    id: 'attr_attenuation_05_text',
  },
  {
    id: 'attr_drive_technology_system_multi_select',
  },
  {
    id: 'attr_stranding_type_simple_select',
  },
  {
    id: 'attr_outer_diameter_max_text',
  },
  {
    id: 'attr_chromatic_dispersion_at_1310_nm_text',
  },
  {
    id: 'attr_chromatic_dispersion_at_1550_nm_text',
  },
  {
    id: 'attr_wave_impedance_approximately_text',
  },
  {
    id: 'attr_wave_impedance_approximately_metric_ohm',
  },
  {
    id: 'attr_weight_metric',
  },
  {
    id: 'housing_material',
  },
  {
    id: 'collar_clamp_material_text',
  },

  {
    id: 'opening_principle',
  },
  {
    id: 'maximum_filling_weight_metric',
  },
  {
    id: 'colour',
  },
  {
    id: 'attr_material_ref',
  },
  {
    id: 'temperature_range_text',
  },
  {
    id: 'echain_system',
  },
  {
    id: 'shipping_weight',
  },
  {
    id: 'attr_coating_text',
  },
  {
    id: 'm4_thread_mounting_ring_bool',
  },
  {
    id: 'large_mounting_ring_bool',
  },
  {
    id: 'spacer_mounting_ring_bool',
  },
  {
    id: 'drivepin_bool',
  },
  {
    id: 'slot_nut_profile_bool',
  },
  {
    id: 'density',
  },
  {
    id: 'max_moisture_absorption',
  },
  { id: 'max_moisture_absorption_test_method_text' },
  {
    id: 'max_water_absorption',
  },
  { id: 'max_water_absorption_test_method_text' },
  {
    id: 'hub_design_simple_select',
  },
  {
    id: 'helix_angle_metric',
  },
  {
    id: 'ip_rating_motor_housing_simple_select',
  },
  {
    id: 'particle_protection_ip41_bool',
  },
  {
    id: 'motors_installation_size_simple_select',
  },
  {
    id: 'motor_connection_text',
  },
  {
    id: 'lubrication_text',
  },
  {
    id: 'motor_brake_bool',
  },
  {
    id: 'hall_bool',
  },
  {
    id: 'motor_encoder_bool',
  },
  {
    id: 'D_W_H_dryve_D3_mm_text',
  },
  {
    id: 'voltage_supply_text',
  },
  {
    id: 'motor_type_simple_select',
  },
  {
    id: 'continuous_motor_current_text',
  },
  {
    id: 'continuous_motor_current_text_area',
  },
  {
    id: 'attr_peak_motor_current_text',
  },
  {
    id: 'max_length_of_cable_metric',
  },
  {
    id: 'ambient_temperature_text',
  },
  {
    id: 'D_W_H_incl_connectors_and_mounting_elements_text',
  },
  {
    id: 'degree_of_soiling_according_EN_61010_text',
  },
  {
    id: 'standstill_current_text',
  },
  {
    id: 'current_draw_logic_text_area',
  },
  {
    id: 'voltage_cycle_direction_input_text_area',
  },
  {
    id: 'input_frequency_text_area',
  },
  {
    id: 'signal_width_cycle_signal_text',
  },
  {
    id: 'signal_width_directional_signal_text',
  },
  {
    id: 'attr_load_power_output_text',
  },
  {
    id: 'boot_up_time_metric',
  },
  {
    id: 'cable_cross_sections_logic_and_power_unit_text_area',
  },
  {
    id: 'ambient_temperature_operation_text',
  },
  {
    id: 'ambient_temperature_transport_text',
  },
  {
    id: 'ambient_temperature_bearing_text',
  },
  {
    id: 'relative_humidity_text',
  },
  {
    id: 'shock_max_metric',
  },
];

const TECHNICAL_DIMENSIONS_ATTRIBUTES: TechnicalDataAttributes = [
  {
    id: 'hi',
  },
  {
    id: 'inner_width',
  },
  {
    id: 'ha',
  },
  {
    id: 'outer_width',
  },
  {
    id: 'bend_radius',
  },
  {
    id: 'pitch_e_chain',
  },
  {
    id: 'links_per_meter',
  },
  {
    id: 'max_cable_diameter_metric',
  },
  {
    id: 'H',
  },
  {
    id: 'D',
  },
  {
    id: 'K',
  },
  {
    id: 'H2',
  },
  {
    id: 'D2_',
  },
  {
    id: 'K2',
  },
  {
    id: 'velocity_FLG_unsupported_max_metric_m_per_s',
  },
  {
    id: 'acceleration_flgmax',
  },
  {
    id: 'maximum_unsupported_length_metric',
  },
  {
    id: 'maximum_long_travel_metric',
  },
  {
    id: 'b2_tooth_width_metric',
  },
  {
    id: 'd1_bore_diameter_metric',
  },
  {
    id: 'attr_max_package_lenght_metric_km',
  },
  {
    id: 'attr_inner_diameter_metric_mm',
  },
  {
    id: 'attr_wall_thickness_metric_mm',
  },
  {
    id: 'attr_outer_diameter_max_metric_mm',
  },
  {
    id: 'attr_weight_metric_kg_km',
  },
  {
    id: 'width',
  },
  {
    id: 'capital_b1_metric',
  },
  {
    id: 'capital_b2',
  },
  {
    id: 'capital_b3_metric',
  },
  {
    id: 'capital_H',
  },
  {
    id: 'lenght', // its a typo coming in from akeneo
  },
  {
    id: 'capital_D',
  },
  {
    id: 'capital_d_tolerance_text',
  },
  {
    id: 'capital_D1',
  },
  {
    id: 'd1',
  },
  {
    id: 'attr_shaft_diameter',
  },
  {
    id: 'lowercase_d',
  },
  {
    id: 'attr_outer_diameter_d2',
  },
  {
    id: 'd2_tolerance',
  },
  {
    id: 'capital_d3_metric',
  },
  {
    id: 'capital_g1_text',
  },
  {
    id: 'capital_g2_text',
  },
  {
    id: 'attr_h_bolt_metric',
  },
  {
    id: 'lowercase_h',
  },
  {
    id: 'capital_h1',
  },
  {
    id: 'attr_capital_h3',
  },
  {
    id: 'attr_capital_l1_metric',
  },
  {
    id: 'capital_l2_metric',
  },
  {
    id: 'capital_t1_text',
  },
  {
    id: 'capital_t2_text',
  },
  {
    id: 'capital_S1',
  },
  {
    id: 'capital_s1_metric',
  },
  {
    id: 'capital_s2_metric',
  },
  {
    id: 'attr_wrench_with_S2',
  },
  {
    id: 'capital_k1_text',
  },
  {
    id: 'capital_k2_text',
  },
  {
    id: 'R1',
  },
  {
    id: 'attr_h1',
  },
  {
    id: 'attr_h3',
  },
  {
    id: 'capital_r2_metric',
  },
  {
    id: 'attr_capital_b',
  },
  {
    id: 'prt_tooth_type_simple_select',
  },
  {
    id: 'lowercase_z',
  },
  {
    id: 'modul_simple_select',
  },
  {
    id: 'd4_pitch_circle_diameter_metric',
  },
  {
    id: 'd5_crown_circle_diameter_metric',
  },
  {
    id: 'b1_overall_length_metric',
  },
  {
    id: 'attr_diameter_d3_metric',
  },
  {
    id: 'attr_b3',
  },
  {
    id: 'attr_flange_thickness_b2',
  },
  {
    id: 'lowercase_d4_metric',
  },
  {
    id: 'lowercase_a',
  },
  {
    id: 'lowercase_b4_metric',
  },
  {
    id: 'attr_bearing_length_b1',
  },
  {
    id: 'd5',
  },
  {
    id: 'attr_lowercase_h2',
  },
  {
    id: 'usable_thread_length_metric',
  },
  {
    id: 'linear_movement_per_step_metric',
  },
  {
    id: 'max_axial_force',
  },
  {
    id: 'attr_thread_size_simple_select',
  },
  {
    id: 'max_feed_rate_metric',
  },
  {
    id: 'attr_diameter_capital_d_metric',
  },
  {
    id: 'diameter_di1_metric',
  },
  {
    id: 'diameter_di2_metric',
  },
  {
    id: 'capital_L',
  },
];

const TECHNICAL_THERMAL_ATTRIBUTES: TechnicalDataAttributes = [
  {
    id: 'max_temperature_long',
  },
  {
    id: 'max_temperature_short',
  },
  {
    id: 'min_temperature',
  },
];

const TECHNICAL_MANUFACTURER_ATTRIBUTES: TechnicalDataAttributes = [
  {
    id: 'manufacturers_name_simpleselect',
  },
  {
    id: 'Manufacturer_Part_No',
  },
  {
    id: 'cable_type',
  },
];

const TECHNICAL_DYNAMIC_ATTRIBUTES: TechnicalDataAttributes = [
  {
    id: 'attr_factor_min_bend_radius_echain_metric_xd',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('eikn7x3sra', 'CF_ICON_Biegeradius.webp', 300, 300)),
  },
  {
    id: 'attr_min_bend_radius_echain_metric_mm',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('eikn7x3sra', 'CF_ICON_Biegeradius.webp', 300, 300)),
  },
  {
    id: 'attr_factor_min_bend_radius_flexible_echain_metric_xd',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('eikn7x3sra', 'CF_ICON_Biegeradius.webp', 300, 300)),
  },
  {
    id: 'attr_min_bend_radius_flexible_echain_metric_mm',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('eikn7x3sra', 'CF_ICON_Biegeradius.webp', 300, 300)),
  },
  {
    id: 'attr_factor_min_bend_radius_fixed_echain_metric_xd',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('eikn7x3sra', 'CF_ICON_Biegeradius.webp', 300, 300)),
  },
  {
    id: 'attr_min_bend_radius_fixed_echain_metric_mm',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('eikn7x3sra', 'CF_ICON_Biegeradius.webp', 300, 300)),
  },
  {
    id: 'attr_temp_echain_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('gfgl7zahmd', 'CF_ICON_Temperatur.webp', 300, 300)),
  },
  {
    id: 'attr_temp_echain_flexible_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('gfgl7zahmd', 'CF_ICON_Temperatur.webp', 300, 300)),
  },
  {
    id: 'attr_temp_echain_fixed_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('gfgl7zahmd', 'CF_ICON_Temperatur.webp', 300, 300)),
  },
  {
    id: 'attr_v_max_unsupported_metric_m_s',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('7yx5rrrjhd', 'CF_ICON_v-max.webp', 300, 300)),
  },
  {
    id: 'attr_v_max_gliding_metric_m_s',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('7yx5rrrjhd', 'CF_ICON_v-max.webp', 300, 300)),
  },
  {
    id: 'attr_a_max_metric_m_s2',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('9yx0smdata', 'Icon_a-max_old.webp', 300, 300)),
  },
  {
    id: 'cf_class_travel_length',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('vjkm1tenyn', 'CF_ICON_Verfahrweg.webp', 300, 300)),
  },
  {
    id: 'attr_v_max_twisted_metric_degree_per_meter',
  },
  {
    id: 'cf_class_torsion',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('ngzzoidpnf', 'CF_ICON_Torsion.webp', 300, 300)),
  },
  {
    id: 'attr_temp_eskin_flat_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('gfgl7zahmd', 'CF_ICON_Temperatur.webp', 300, 300)),
  },
  {
    id: 'attr_temp_eskin_flat_min_metric_celsius',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('gfgl7zahmd', 'CF_ICON_Temperatur.webp', 300, 300)),
  },
  {
    id: 'attr_temp_eskin_flat_max_metric_celsius',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('gfgl7zahmd', 'CF_ICON_Temperatur.webp', 300, 300)),
  },
  {
    id: 'attr_min_bend_radius_eskin_flat_metric_mm',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('eikn7x3sra', 'CF_ICON_Biegeradius.webp', 300, 300)),
  },
  {
    id: 'attr_travel_distance_eskin_flat_metric_m',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('eikn7x3sra', 'CF_ICON_Biegeradius.webp', 300, 300)),
  },
];

const TECHNICAL_ELECTRICITY_ATTRIBUTES: TechnicalDataAttributes = [
  {
    id: 'nominal_voltage_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('qgjqqvucxa', 'CF_ICON_NennspannungPNG.webp', 300, 300)),
  },
  {
    id: 'attr_testing_voltage_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('smp31ihjfd', 'CF_ICON_Pr%C3%BCfspannung.webp', 300, 300)),
  },
];

const TECHNICAL_CORE_ATTRIBUTES: TechnicalDataAttributes = [
  {
    id: 'attr_conductor_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('hu3h5gi6pm', 'CF_ICON_Leiter.webp', 300, 300)),
  },
  {
    id: 'attr_core_insulation_text_area',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('j5f20u9r2k', 'CF_ICON_Aderisolation.webp', 300, 300)),
  },
  {
    id: 'attr_core_structure_simple_select',
    icon: 'ai444.png',
  },
  {
    id: 'attr_core_identification_core_identification_record',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('e6foe9iyzp', 'CF_ICON_Aderkennzeichnung.webp', 300, 300)),
  },
  {
    id: 'attr_intermediate_layer_simple_select',
    icon: 'ai681.png',
  },
  {
    id: 'attr_inner_jacket_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('hl6lvviva2', 'Icon_shield_old.webp', 300, 300)),
  },
  {
    id: 'attr_overall_shield_simple_select',
    icon: 'ai447.png',
  },
  {
    id: 'attr_element_shield_simple_select',
    icon: 'ai447.png',
  },
  {
    id: 'attr_outer_jacket_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('iszz3ga8bo', 'CF_ICON_Au%C3%9Fenmantel.webp', 300, 300)),
  },
  {
    id: 'attr_jacket_colour_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('iszz3ga8bo', 'CF_ICON_Au%C3%9Fenmantel.webp', 300, 300)),
  },
  {
    id: 'attr_outer_jacket_text_area',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('iszz3ga8bo', 'CF_ICON_Au%C3%9Fenmantel.webp', 300, 300)),
  },
  {
    id: 'attr_cfrip_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(
      new WidenImageConfig('sboszvs41s', 'CFRIP-Logo_ohne_Design_Award.webp', 300, 300)
    ),
  },
  {
    id: 'attr_film_material_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('iszz3ga8bo', 'CF_ICON_Au%C3%9Fenmantel.webp', 300, 300)),
  },
  {
    id: 'attr_colour_outer_film',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('iszz3ga8bo', 'CF_ICON_Au%C3%9Fenmantel.webp', 300, 300)),
  },
];

export const TECHNICAL_CHARACTERISTICS_CLASSIFICATION_ATTRIBUTES: TechnicalDataAttributes = [
  {
    id: 'attr_uv_resistance_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('cng8cafywn', 'Icon_UV-resistant_old.webp', 300, 300)),
  },
  {
    id: 'attr_oil_resistance_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('5jljnp11bp', 'Icon_oilcan_old.webp', 300, 300)),
  },
  {
    id: 'attr_silicone_free_text',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('l1eqheshw9', 'Icon_silicone-free_old.webp', 300, 300)),
  },
  {
    id: 'attr_halogen_free_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('zmqr0rovys', 'Icon_halogen-free_old.webp', 300, 300)),
  },
  {
    id: 'attr_eac_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('pzcxqngxnu', 'Picto_EAC.webp', 300, 300)),
  },
  {
    id: 'reach_text',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('kpo0cg54st', 'Icon_REACH.webp', 300, 300)),
  },
  {
    id: 'attr_lead_free_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('kzhp1xphbl', 'Icon_RoHS_HKS7.webp', 300, 300)),
  },
  {
    id: 'attr_clean_room_text',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('qawjqj9vsp', 'Picto_cleanroom.webp', 300, 300)),
  },
  {
    id: 'attr_ce_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('6dncbmcvmr', 'Picto_CE.webp', 300, 300)),
  },
  {
    id: 'ul_csa_reference_entity_single_link',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('nopycjcxg9', 'Picto_cURus.webp', 300, 300)),
  },
  {
    id: 'ul_awm_reference_entity_single_link',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('mvscbyhhey', 'ICON_UL_AWM_round.webp', 300, 300)),
  },
  {
    id: 'ul_verified_text',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('upkpxgmnhk', 'CF_ICON_ul.verified.webp', 300, 300)),
  },
  {
    id: 'attr_ul_listed_text',
    fallbackIcon: WidenUtil.getWidenCDNUrl(
      new WidenImageConfig('f3qdfthu7s', 'ICON_official_UL-listed.webp', 300, 300)
    ),
    replaceIconMap: {
      CF150_UL: WidenUtil.getWidenCDNUrl(
        new WidenImageConfig('zhayen0ass', 'CF_UL_Listed_CF150UL_CF160UL.webp', 300, 300)
      ),
      CF160_UL: WidenUtil.getWidenCDNUrl(
        new WidenImageConfig('zhayen0ass', 'CF_UL_Listed_CF150UL_CF160UL.webp', 300, 300)
      ),
    },
  },
  {
    id: 'attr_cei_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('ohlgdsb5at', 'Picto_CEI.webp', 300, 300)),
  },
  {
    id: 'attr_ctp_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('kfymjmzpll', 'Picto_CTP.webp', 300, 300)),
  },
  {
    id: 'attr_desina_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('d7cygznlbj', 'Picto_DESINA.webp', 300, 300)),
  },
  {
    id: 'attr_flame_retardant_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('wq7h7tdk6j', 'Icon_flame-retardant_old.webp', 300, 300)),
  },
  {
    id: 'attr_dnv_gl_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(
      new WidenImageConfig('twbfsaaabk', 'Maritime_certification_mark_CMYK.webp', 300, 300)
    ),
  },
  {
    id: 'attr_nfpa_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('dfsd4vsymw', 'Icon_NFPA_old_HKS7.webp', 300, 300)),
  },
  {
    id: 'attr_offshore_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('1ahtgolyac', 'Icon_offshore_old.webp', 300, 300)),
  },
  {
    id: 'attr_clpa_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('j4tqslgr34', 'ICON_official_CLPA.webp', 300, 300)),
  },
  {
    id: 'attr_ukca_simple_select',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('pvmuyxower', 'UKCA%20logo%20-%20fill.webp', 300, 300)),
  },
  {
    id: 'rohs_compliance',
  },
];

export const TECHNICAL_ECHAIN_ICON_ATTRIBUTES: TechnicalDataAttributes = [
  {
    id: 'hi',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('rkjf5nucx9', 'ECS_ICON_inner_height_1.webp', 300, 300)),
  },
  {
    id: 'max_cable_diameter_metric',
    widenUrl: WidenUtil.getWidenCDNUrl(
      new WidenImageConfig('xrhiywpyqt', 'ECS_ICON_maximum_cable_diameter_1.webp', 300, 300)
    ),
  },
  {
    id: 'opening_principle',
    widenUrl: WidenUtil.getWidenCDNUrl(
      new WidenImageConfig('d1poe0wojw', 'ECS_PICTO_openable_outer_radius_DE_1.webp', 300, 300)
    ),
  },
  {
    id: 'inner_width',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('iognpvw5s3', 'ECS_ICON_inner_width_1.webp', 300, 300)),
  },
  {
    id: 'bend_radius',
    widenUrl: WidenUtil.getWidenCDNUrl(new WidenImageConfig('ib1qbrq96x', 'ECS_ICON_partition_1.webp', 300, 300)),
  },
];

const TECHNICAL_PLUG_ATTRIBUTES: TechnicalDataAttributes = [
  { id: 'connector_A_simple_select' },
  { id: 'connector_B_simple_select' },
  { id: 'manufactorer_connector_A' },
  { id: 'manufactorer_connector_B' },
  { id: 'plug_type_connector_text' },
  { id: 'handle_base_text' },
  { id: 'union_nut_screw_text' },
  { id: 'contact_base_text' },
  { id: 'contacts_text' },
  { id: 'seal_text' },
  { id: 'number_of_poles_simple_select' },
  { id: 'ampacity_metric' },
  { id: 'rated_voltage_metric' },
  { id: 'insulating_resistance_text' },
  { id: 'contact_resistance_text' },
  { id: 'pollution_degree_text' },
  { id: 'ambient_temperature_connector_min_metric' },
  { id: 'ambient_temperature_connector_max_metric' },
  { id: 'protection_class_text' },
  { id: 'mechanical_service_life_text' },
  { id: 'insert_1_harting_part_no_text' },
  { id: 'insert_2_harting_part_no_text' },
  { id: 'housing_1_harting_part_no_text' },
  { id: 'housing_2_harting_part_no_text' },
  { id: 'housing_series_text' },
  { id: 'plug_design' },
  { id: 'locking_type_text' },
];

const TECHNICAL_TOLERANCE_ATTRIBUTES: TechnicalDataAttributes = [{ id: 'lowercase_d1_tolerance_text' }];

const DRAWING_ATTRIBUTES: TechnicalDataAttributes = [
  {
    id: 'unsupported_length',
    assetAttributeId: 'FLG_FLG_chart_01',
  },
  {
    id: 'installation_dimension_drawing_02_description',
    assetAttributeId: 'installation_dimension_drawing_02',
  },
  {
    id: 'installation_dimension_drawing_03_description',
    assetAttributeId: 'installation_dimension_drawing_03',
  },
];

export interface LabelValue {
  label: string;
  value: string | boolean;
}

export const getAllTechnicalDataAttributes = () =>
  // Important: When adding new categories for technical data attributes, make sure to add them here as well!
  TECHNICAL_MECHANICAL_ATTRIBUTES.concat(
    TECHNICAL_REQUIREMENTS_ATTRIBUTES,
    TECHNICAL_GENERAL_ATTRIBUTES,
    TECHNICAL_DIMENSIONS_ATTRIBUTES,
    TECHNICAL_THERMAL_ATTRIBUTES,
    TECHNICAL_DYNAMIC_ATTRIBUTES,
    TECHNICAL_ELECTRICITY_ATTRIBUTES,
    TECHNICAL_CORE_ATTRIBUTES,
    TECHNICAL_CHARACTERISTICS_CLASSIFICATION_ATTRIBUTES,
    TECHNICAL_PLUG_ATTRIBUTES,
    TECHNICAL_MANUFACTURER_ATTRIBUTES,
    TECHNICAL_CHAINFLEX_CLASS_ID_ATTRIBUTES,
    TECHNICAL_CHAINFLEX_CLASS_LABEL_ATTRIBUTES
  );

export const getAllTechnicalDataAttributeNames = () =>
  getAllTechnicalDataAttributes().map(technicalAttribute => technicalAttribute.id);

const TECHNICAL_ELECTRICAL_MOTOR_ATTRIBUTES: TechnicalDataAttributes = [{ id: 'nominal_current_metric' }];

export function getAttributeValueByAttributeDefinitions<T>(
  attribute: ProductAttribute<T>,
  attributeDefinitions?: AttributeDefinitionFragment[],
  attributeKey?: string
): string | T {
  const attributeValue = getAttributeValue<T>(attribute);
  // when there is an attribute definition for this attribute, check if it has a translated attribute value matching the value key,
  // if not, just use the value (or value.value) itself
  const attributeDefinition = attributeDefinitions?.find(item => item.attribute_id === attributeKey);
  const attributeDefinitionValue = attributeDefinition?.attribute_values?.find(
    item => (item.value_key as unknown as T) === attributeValue
  );
  const labeledAttributeValue = attributeDefinitionValue?.label || attributeValue;

  if (attributeDefinition?.typename === 'LOCALIZED_ENUM' || attributeDefinition?.typename === 'BOOLEAN') {
    return labeledAttributeValue;
  }

  return ProductHelper.removeTrailingZeros(labeledAttributeValue as unknown as string);
}

const getTechnicalDataIcon = (
  technicalDataAttribute: TechnicalDataAttribute | TechnicalDataAttributeLegacyConfig
): string | null => {
  if (isTechnicalDataAttributeAndNotLegacyConfig(technicalDataAttribute)) {
    return technicalDataAttribute.widenUrl ?? null;
  }

  let icon: string | null = null;
  // handling for legacy technical-data configuration
  if ('icon' in technicalDataAttribute && technicalDataAttribute.icon) {
    icon = `https://www.igus.de/contentData/Products/AttributeIcons/${technicalDataAttribute.icon}`;
  }

  if ('replaceIconMap' in technicalDataAttribute) {
    let productName: string | null = null;
    if (typeof window !== 'undefined') {
      productName = Router.query.product as string;
    }
    if (productName && technicalDataAttribute.replaceIconMap?.[productName]) {
      icon = technicalDataAttribute.replaceIconMap?.[productName];
    } else {
      icon = technicalDataAttribute.fallbackIcon;
    }
  }

  return icon;
};

function toTechnicalData(
  unitSystem: UnitSystem,
  product: ProductFragmentEnhanced,
  attributeDefinitions: AttributeDefinitionFragment[],
  colourCodeDefinitions?: Cf_Colour_Codes[],
  driveTechnologySystemDefinitions?: AttributeDefinitionFragment
): (value: TechnicalDataAttribute | TechnicalDataAttributeLegacyConfig) => Nullable<TechnicalData> {
  return value => {
    let rawAttributeValue = product.attributes?.[value.id];
    if (rawAttributeValue == null || rawAttributeValue === '') {
      return null;
    }

    if (value.id.includes('shipping_weight') && (rawAttributeValue as LabelValue).value === '-1.0000') {
      return null;
    }

    const attributeMultiValues = String(
      getAttributeValueByAttributeDefinitions<string[]>(
        rawAttributeValue as ProductAttribute<string[]>,
        attributeDefinitions,
        value.id
      )
    )?.split(','); // To support multiselect attributes

    // Extract matching value from colourCodeDefinitions
    if (value.id.includes('colour_code')) {
      const result: string[] = [];
      attributeMultiValues.forEach(attributeMultiValue => {
        const colorCode = colourCodeDefinitions?.find(colourCode => colourCode.id === attributeMultiValue);
        result.push(colorCode?.label ?? attributeMultiValue);
      });
      rawAttributeValue = result.join(', ');
    }

    // Extract matching value from driveTechnologySystemDefinitions
    if (value.id === 'attr_drive_technology_system_multi_select') {
      const result: string[] = [];
      attributeMultiValues.forEach(attributeValues => {
        const driveTechnologySystem = driveTechnologySystemDefinitions.attribute_values.find(
          item => item.value_key === attributeValues
        );
        result.push(driveTechnologySystem?.label ?? attributeValues);
      });
      rawAttributeValue = result.join(', ');
    }

    const attributeValueByDefinition = getAttributeValueByAttributeDefinitions<ProductAttributeValueAmountUnit>(
      rawAttributeValue as ProductAttribute<ProductAttributeValueAmountUnit>,
      attributeDefinitions,
      value.id
    );

    const attributeDefinition = attributeDefinitions?.find(item => item.attribute_id === value.id);

    let newDescription = value.id;
    if (attributeDefinition?.label !== null && attributeDefinition?.label !== undefined) {
      newDescription = attributeDefinition.label;
    } else if (isLabeledAttribute<ProductAttributeUnion>(rawAttributeValue)) {
      newDescription = rawAttributeValue.label;
    }

    const mediaElement =
      'assetAttributeId' in value
        ? ProductHelper.getAssetsMedia(product, value.assetAttributeId)?.productMedia?.[0]
        : null;

    return {
      description: newDescription ?? null,
      value: getAttributeDisplayValue(unitSystem, attributeValueByDefinition, attributeDefinition) || null,
      icon: getTechnicalDataIcon(value) ?? null,
      mediaElement: mediaElement || null,
    };
  };
}

export function extractTechnicalData(
  unitSystem: UnitSystem,
  product: ProductFragmentEnhanced,
  technicalDrawingsAttributes: TechnicalDataAttributes,
  attributeDefinitions: AttributeDefinitionFragment[],
  colourCodeDefinitions: Cf_Colour_Codes[],
  driveTechnologySystemDefinitions: AttributeDefinitionFragment
) {
  return technicalDrawingsAttributes
    .map(
      toTechnicalData(
        unitSystem,
        product,
        attributeDefinitions,
        colourCodeDefinitions,
        driveTechnologySystemDefinitions
      )
    )
    .filter(Boolean);
}

/**
 * Extracts data which should be displayed in the table below technical drawings from the attributes of the given variant. The given master variant
 * is used as fallback if the attribute is not set in the variant. The function accepts a string array of attribute keys to which the table is limited.
 */
export function extractDrawingsTechnicalData(
  unitSystem: UnitSystem,
  product: ProductFragmentEnhanced,
  attributeKeys: string[],
  attributeDefinitions: AttributeDefinitionFragment[],
  colourCodeDefinitions: Cf_Colour_Codes[],
  driveTechnologySystemDefinitions: AttributeDefinitionFragment
): TechnicalData[] {
  const technicalDrawingsAttributes = attributeKeys.map(attributeKey => ({ id: attributeKey }));
  return extractTechnicalData(
    unitSystem,
    product,
    technicalDrawingsAttributes,
    attributeDefinitions,
    colourCodeDefinitions,
    driveTechnologySystemDefinitions
  );
}

export const generateTechnicalDataCategories = (
  unitSystem: UnitSystem,
  currentProduct: ProductFragmentEnhanced,
  data: TechnicalDataRecordFragmentEnhanced[] | LegacyTechnicalDataConfigEntry[],
  attributeDefinitions: AttributeDefinitionFragment[],
  colourCodeDefinitions: Cf_Colour_Codes[],
  driveTechnologySystemDefinitions: AttributeDefinitionFragment
): TechnicalDataCategory[] =>
  data.map((record: TechnicalDataRecordFragmentEnhanced | LegacyTechnicalDataConfigEntry) => ({
    name: record.name,
    label: record.label,
    attributes: extractTechnicalData(
      unitSystem,
      currentProduct ?? null,
      (record.attributes as TechnicalDataAttributes) ?? null,
      attributeDefinitions ?? null,
      colourCodeDefinitions ?? null,
      driveTechnologySystemDefinitions ?? null
    ),
  }));

export type LegacyTechnicalDataConfigEntry = {
  name?: string | null | undefined;
  label?: string | null | undefined;
  attributes?: TechnicalDataAttributes | null | undefined;
};

export const technicalDataDrawingsProperties = {
  name: 'drawings_properties',
  label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.DRAWINGS_TITLE',
  attributes: DRAWING_ATTRIBUTES,
};

export const legacyTechnicalDataConfig: LegacyTechnicalDataConfigEntry[] = [
  {
    name: 'manufacturer_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.MANUFACTURER_TITLE',
    attributes: TECHNICAL_MANUFACTURER_ATTRIBUTES,
  },
  {
    name: 'plugs_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.PLUGS_TITLE',
    attributes: TECHNICAL_PLUG_ATTRIBUTES,
  },
  {
    name: 'general_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.GENERAL_TITLE',
    attributes: TECHNICAL_GENERAL_ATTRIBUTES,
  },
  {
    name: 'dimensions_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.DIMENSIONS_TITLE',
    attributes: TECHNICAL_DIMENSIONS_ATTRIBUTES,
  },
  {
    name: 'electrical_motor_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.ELECTRICAL_MOTOR_TITLE',
    attributes: TECHNICAL_ELECTRICAL_MOTOR_ATTRIBUTES,
  },
  {
    name: 'thermal_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.THERMAL_TITLE',
    attributes: TECHNICAL_THERMAL_ATTRIBUTES,
  },
  {
    name: 'mechanical_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.MECHANICAL_TITLE',
    attributes: TECHNICAL_MECHANICAL_ATTRIBUTES,
  },
  {
    name: 'requirements_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.REQUIREMENTS_TITLE',
    attributes: TECHNICAL_REQUIREMENTS_ATTRIBUTES,
  },
  {
    name: 'dynamic_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.DYNAMIC_TITLE',
    attributes: TECHNICAL_DYNAMIC_ATTRIBUTES,
  },
  {
    name: 'core_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.CABLE_STRUCTURE_TITLE',
    attributes: TECHNICAL_CORE_ATTRIBUTES,
  },
  {
    name: 'electrical_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.ELECTRICAL_TITLE',
    attributes: TECHNICAL_ELECTRICITY_ATTRIBUTES,
  },
  {
    name: 'tolerances_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.TOLERANCES_TITLE',
    attributes: TECHNICAL_TOLERANCE_ATTRIBUTES,
  },
  {
    name: 'charateristics_classification_properties',
    label: 'PRODUCT_DETAILS.ATTRIBUTE_GROUPS.PROPERTIES_TITLE',
    attributes: TECHNICAL_CHARACTERISTICS_CLASSIFICATION_ATTRIBUTES,
  },
  technicalDataDrawingsProperties,
];
